// The Space site's Plans page (`/workspace/<slug>/plan`) and Domain page (`/workspace/<slug>/domain`), IN Symphony
// (spec 2026-10-25, plan-domain): the site strip's Upgrade and Get <domain> open them instead of Wix. Paying stays in
// Wix's checkout: the only link out is the server's buy url behind "Continue to payment ↗". Pure and import-free, so
// the jest suite can load it without the app's dependency graph. Every rule here is pinned by the shared vectors in
// `native-apps/shared/site-plans/contract.json`, which iOS `SitePlans.swift` and Android `SitePlans.kt` run too —
// change one, change all three.
//
// The wire shapes are symphony-bff's `GetSitePlansResponse` / `SearchSiteDomainsResponse` (VMR
// individual/docs/2026-10-25-site-plans-and-domains.md) as /api/site-plans and /api/site-domains pass them through, and
// as the phones read them from the BFF: JSON, camelCase, enums by name, int64 and every price a decimal STRING.
// `known: false` is a failed read: never invented prices — the page sends the reader to Wix instead (`wixFallbackUrl`).

/* -------------------------------------------------------------------------------------------- shared */

/** A wire number (number or numeric string); anything else, or a negative, is 0. */
function wireNumber(v: unknown): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function text(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/** Half up to 2 decimals. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** A currency code: a string, or an object's `code`; upper-cased; '' when absent. */
function currencyCode(v: unknown): string {
  const code = typeof v === 'string' ? v : v && typeof v === 'object' ? (v as { code?: unknown }).code : '';
  return typeof code === 'string' ? code.trim().toUpperCase() : '';
}

/**
 * The server's buy url, opened only when it is https on wix.com or a subdomain of it; '' otherwise (no Continue
 * button). The ONE link out of these pages.
 */
export function checkoutUrl(url: unknown): string {
  const raw = text(url);
  if (!raw) return '';
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return '';
  }
  const host = parsed.hostname.toLowerCase();
  if (parsed.protocol.toLowerCase() !== 'https:' || parsed.username || parsed.password) return '';
  return host === 'wix.com' || host.endsWith('.wix.com') ? raw : '';
}

/** Whole amounts with no decimals, anything else with exactly 2. */
export function priceFractionDigits(amount: number): 0 | 2 {
  return Math.abs(amount - Math.round(amount)) < 0.005 ? 0 : 2;
}

const formatters = new Map<string, Intl.NumberFormat>();

/** A price in `locale`: the currency's symbol when a code is given, else a plain grouped number. Formatters cached. */
export function formatPrice(amount: number, currency: string, locale: string): string {
  const digits = priceFractionDigits(amount);
  const id = `${locale}|${currency}|${digits}`;
  let f = formatters.get(id);
  if (!f) {
    const options: Intl.NumberFormatOptions = { minimumFractionDigits: digits, maximumFractionDigits: digits };
    try {
      f = new Intl.NumberFormat(locale, currency ? { ...options, style: 'currency', currency } : options);
    } catch {
      f = new Intl.NumberFormat('en-US', options);
    }
    formatters.set(id, f);
  }
  return f.format(amount);
}

/* -------------------------------------------------------------------------------------------- plans: wire */

export type PlanTier = 'LIGHT' | 'CORE' | 'BUSINESS' | 'BUSINESS_ELITE';
export type PlanCycle = 'MONTHLY' | 'YEARLY';

type WireNumber = number | string;

export interface PlanPriceWire {
  cycle?: string;
  price?: WireNumber;
  monthlyEquivalent?: WireNumber;
  salePrice?: WireNumber;
  saleMonthlyEquivalent?: WireNumber;
  currency?: string;
  taxNote?: string;
  buyUrl?: string;
}

export interface PlanHighlightsWire {
  storageGb?: WireNumber;
  storageUnlimited?: boolean;
  videoMinutes?: WireNumber;
  videoUnlimited?: boolean;
  collaborators?: WireNumber;
  removeAds?: boolean;
  connectDomain?: boolean;
  acceptPayments?: boolean;
  freeDomainFirstYear?: boolean;
}

export interface PlanWire {
  productId?: string;
  tier?: string;
  name?: string;
  tagline?: string;
  prices?: PlanPriceWire[];
  highlights?: PlanHighlightsWire;
  current?: boolean;
  recommended?: boolean;
}

export interface CurrentPlanWire {
  productId?: string;
  tier?: string;
  name?: string;
  cycle?: string;
  periodEndsAt?: string;
  autoRenew?: boolean;
}

export interface SiteCurrencyWire {
  code?: string;
  symbol?: string;
}

export interface SitePlansWire {
  known?: boolean | null;
  current?: CurrentPlanWire | null;
  premium?: boolean;
  plans?: PlanWire[];
  currency?: string | SiteCurrencyWire;
  taxNote?: string;
  metaSiteId?: string;
  readAt?: string;
}

/* -------------------------------------------------------------------------------------------- plans: model */

export const PLAN_TIERS: PlanTier[] = ['LIGHT', 'CORE', 'BUSINESS', 'BUSINESS_ELITE'];
export const PLAN_CYCLES: PlanCycle[] = ['MONTHLY', 'YEARLY'];
const CYCLE_MONTHS: Record<PlanCycle, number> = { MONTHLY: 1, YEARLY: 12 };

export interface PlanPrice {
  cycle: PlanCycle;
  total: number;
  perMonth: number;
  /** The regular per-month price when a sale price is shown; 0 for none. */
  wasPerMonth: number;
  buyUrl: string;
}

export type PlanBadge = 'current' | 'recommended' | null;

export interface SitePlan {
  productId: string;
  tier: PlanTier;
  name: string;
  prices: Partial<Record<PlanCycle, PlanPrice>>;
  isCurrent: boolean;
  isRecommended: boolean;
  badge: PlanBadge;
  highlights: PlanHighlightsWire;
}

export interface CurrentPlan {
  productId: string;
  tier: string;
  name: string;
  /** MONTHLY / YEARLY / YEAR2 / YEAR3; '' when unknown. */
  cycle: string;
  /** When the billing period ends ('' when Wix did not say). */
  periodEndsAt: string;
  /** false: the plan ENDS at `periodEndsAt` instead of renewing. */
  autoRenew: boolean;
}

/** Whether the prices include tax: '' when the server did not say (no tax line). */
export type TaxNote = 'included' | 'excluded' | '';

export type SitePlansState = 'error' | 'empty' | 'ready';

/** Why a page could not load: the server answered `known: false`, or no answer came at all; '' = it loaded. */
export type SiteFailure = 'known_false' | 'no_answer' | '';

function failureOf(wire: { known?: boolean | null } | null | undefined): SiteFailure {
  if (!wire) return 'no_answer';
  return wire.known === true ? '' : 'known_false';
}

export interface SitePlans {
  state: SitePlansState;
  currency: string;
  taxNote: TaxNote;
  /** The site has a Premium plan (a current plan of an unknown tier reads "Premium"). */
  premium: boolean;
  current: CurrentPlan | null;
  plans: SitePlan[];
  cycles: PlanCycle[];
  defaultCycle: PlanCycle;
  /** The largest yearly saving over the plans, a whole percent; null for none. */
  saving: number | null;
  /** The plan selected when the page opens; '' for none. */
  defaultProductId: string;
  /** Why the page could not load ('' = it did): the reason the page sends the reader to Wix ({@link wixFallbackUrl}). */
  failure: SiteFailure;
  /** The business site the answer is about, as the server named it (also on `known: false`); '' when absent. */
  metaSiteId: string;
}

/** One price row for the page's two cycles; null for another cycle or no price. */
export function cyclePrice(row: PlanPriceWire | null | undefined): PlanPrice | null {
  const cycle = text(row?.cycle) as PlanCycle;
  if (!PLAN_CYCLES.includes(cycle)) return null;
  const price = wireNumber(row?.price);
  const sale = wireNumber(row?.salePrice);
  const onSale = sale > 0 && sale < price;
  const total = onSale ? sale : price;
  if (total <= 0) return null;
  const months = CYCLE_MONTHS[cycle];
  // Wix's own per-month figure when the server sent one, else the cycle's price over its months.
  const regularPerMonth = wireNumber(row?.monthlyEquivalent) || round2(price / months);
  const perMonth = onSale ? wireNumber(row?.saleMonthlyEquivalent) || round2(sale / months) : regularPerMonth;
  return { cycle, total, perMonth, wasPerMonth: onSale ? regularPerMonth : 0, buyUrl: checkoutUrl(row?.buyUrl) };
}

/** What paying yearly saves per month against monthly, a whole percent; null when there is no saving. */
export function planSaving(monthly: number, yearly: number): number | null {
  if (!(monthly > 0) || !(yearly > 0) || yearly >= monthly) return null;
  return Math.floor(((monthly - yearly) / monthly) * 100 + 0.5);
}

function savingOf(plan: SitePlan): number | null {
  return planSaving(plan.prices.MONTHLY?.perMonth ?? 0, plan.prices.YEARLY?.perMonth ?? 0);
}

export interface I18nLine {
  key: string;
  values?: Record<string, string>;
}

const TIER_BULLETS: Partial<Record<PlanTier, string>> = { BUSINESS: 'ecommerceStandard', BUSINESS_ELITE: 'ecommerceAdvanced' };

/** A plan card's bullets: Symphony's own copy (Wix's curated bullets have no server API) with the catalog's numbers, at most 5. */
export function planBullets(plan: { tier?: string; highlights?: PlanHighlightsWire | null }): I18nLine[] {
  const F = 'sitePlans.feature.';
  const h = plan.highlights ?? {};
  const lines: I18nLine[] = [];
  const own = TIER_BULLETS[plan.tier as PlanTier];
  if (own) lines.push({ key: `${F}${own}` });
  if (h.acceptPayments === true) lines.push({ key: `${F}payments` });
  if (h.removeAds === true) lines.push({ key: `${F}noAds` });
  if (h.freeDomainFirstYear === true) lines.push({ key: `${F}freeDomain` });
  else if (h.connectDomain === true) lines.push({ key: `${F}domain` });
  const gb = Math.floor(wireNumber(h.storageGb));
  if (h.storageUnlimited === true) lines.push({ key: `${F}storageUnlimited` });
  else if (gb > 0) lines.push({ key: `${F}storage`, values: { gb: String(gb) } });
  const minutes = Math.floor(wireNumber(h.videoMinutes));
  if (h.videoUnlimited === true) lines.push({ key: `${F}videoUnlimited` });
  else if (minutes > 0)
    lines.push(minutes % 60 === 0 ? { key: `${F}video`, values: { hours: String(minutes / 60) } } : { key: `${F}videoMinutes`, values: { minutes: String(minutes) } });
  return lines.slice(0, 5);
}

const KNOWN_CYCLES = new Set(['MONTHLY', 'YEARLY', 'YEAR2', 'YEAR3']);

function parseCurrent(wire: CurrentPlanWire | null | undefined): CurrentPlan | null {
  const productId = text(wire?.productId);
  const tier = text(wire?.tier);
  if (!wire || (!productId && !PLAN_TIERS.includes(tier as PlanTier))) return null;
  const cycle = text(wire.cycle);
  return {
    productId,
    tier: PLAN_TIERS.includes(tier as PlanTier) ? tier : '',
    name: text(wire.name),
    cycle: KNOWN_CYCLES.has(cycle) ? cycle : '',
    periodEndsAt: text(wire.periodEndsAt),
    autoRenew: wire.autoRenew !== false,
  };
}

function taxNoteOf(v: unknown): TaxNote {
  return v === 'INCLUDES_TAX' ? 'included' : v === 'EXCLUDES_TAX' ? 'excluded' : '';
}

/** The whole GetSitePlans answer as the page draws it (null = the request failed: the error state). */
export function parseSitePlans(wire: SitePlansWire | null | undefined): SitePlans {
  const known = wire?.known === true;
  const current = known ? parseCurrent(wire?.current) : null;
  const byTier = new Map<PlanTier, SitePlan>();
  for (const p of known ? (wire?.plans ?? []) : []) {
    const tier = text(p?.tier) as PlanTier;
    if (!PLAN_TIERS.includes(tier) || byTier.has(tier)) continue;
    const prices: Partial<Record<PlanCycle, PlanPrice>> = {};
    for (const row of p.prices ?? []) {
      const price = cyclePrice(row);
      if (price && !prices[price.cycle]) prices[price.cycle] = price;
    }
    if (!prices.MONTHLY && !prices.YEARLY) continue;
    const productId = text(p.productId);
    const isCurrent = p.current === true || (current !== null && productId !== '' && productId === current.productId);
    const isRecommended = p.recommended === true;
    byTier.set(tier, {
      productId,
      tier,
      name: text(p.name),
      prices,
      isCurrent,
      isRecommended,
      badge: isCurrent ? 'current' : isRecommended ? 'recommended' : null,
      highlights: p.highlights ?? {},
    });
  }
  const plans = PLAN_TIERS.filter((t) => byTier.has(t)).map((t) => byTier.get(t) as SitePlan);
  const cycles = PLAN_CYCLES.filter((c) => plans.some((p) => p.prices[c]));
  const savings = plans.map(savingOf).filter((s): s is number => s !== null);
  const recommended = plans.find((p) => p.isRecommended && !p.isCurrent);
  const currentIndex = plans.findIndex((p) => p.isCurrent);
  const next = currentIndex >= 0 ? plans[currentIndex + 1] : undefined;
  return {
    state: !known ? 'error' : plans.length === 0 ? 'empty' : 'ready',
    currency: known ? currencyCode(wire?.currency) : '',
    taxNote: known ? taxNoteOf(wire?.taxNote) : '',
    premium: known && (wire?.premium === true || current !== null),
    current,
    plans,
    cycles,
    defaultCycle: cycles.length > 0 && !cycles.includes('YEARLY') ? 'MONTHLY' : 'YEARLY',
    saving: savings.length > 0 ? Math.max(...savings) : null,
    defaultProductId: recommended?.productId ?? next?.productId ?? '',
    failure: failureOf(wire),
    metaSiteId: text(wire?.metaSiteId),
  };
}

/** The plan saving for a card (null for none). */
export function sitePlanSaving(plan: SitePlan): number | null {
  return savingOf(plan);
}

/** The plan can be bought on `cycle`: a buy url for it, and it is not what the site already has. */
export function canBuy(plans: SitePlans, plan: SitePlan, cycle: PlanCycle): boolean {
  if (!plan.prices[cycle]?.buyUrl) return false;
  if (!plan.isCurrent) return true;
  const currentCycle = plans.current?.cycle ?? '';
  return currentCycle !== '' && currentCycle !== cycle;
}

/* -------------------------------------------------------------------------------------------- domains */

export interface DomainResultWire {
  domain?: string;
  available?: boolean;
  priceFirstYear?: WireNumber;
  priceRenewal?: WireNumber;
  currency?: string;
  freeWithYearlyPlan?: boolean;
  buyUrl?: string;
  /** The name typed in the query (the server lists it first). */
  exact?: boolean;
}

export interface SiteDomainsWire {
  known?: boolean | null;
  currency?: string | SiteCurrencyWire;
  taxNote?: string;
  premium?: boolean;
  query?: string;
  connectedDomain?: string;
  results?: DomainResultWire[];
  metaSiteId?: string;
}

export type DomainStatus = 'taken' | 'free' | 'price' | 'available';

export interface DomainRow {
  domain: string;
  status: DomainStatus;
  priceFirstYear: number;
  priceRenewal: number;
  buyUrl: string;
  selectable: boolean;
}

export interface SiteDomains {
  state: SitePlansState;
  currency: string;
  /** What the server searched (the field's prefill). */
  query: string;
  connectedDomain: string;
  rows: DomainRow[];
  defaultDomain: string;
  failure: SiteFailure;
  metaSiteId: string;
}

export const DOMAIN_QUERY_MAX = 63;
export const DOMAIN_RESULTS_MAX = 10;

/** What the page sends as the search ('' = the server's suggestions for the site's name). */
export function domainQuery(raw: unknown): string {
  let q = (typeof raw === 'string' ? raw : '').trim().toLowerCase();
  q = q.replace(/^https?:\/\//, '').replace(/^www\./, '');
  const slash = q.indexOf('/');
  if (slash >= 0) q = q.slice(0, slash);
  return q.replace(/\s+/g, ' ').slice(0, DOMAIN_QUERY_MAX).trim();
}

function domainStatus(available: boolean, free: boolean, firstYear: number): DomainStatus {
  if (!available) return 'taken';
  if (free) return 'free';
  return firstYear > 0 ? 'price' : 'available';
}

/** The whole SearchSiteDomains answer as the page draws it (null = the request failed: the error state). */
export function parseSiteDomains(wire: SiteDomainsWire | null | undefined): SiteDomains {
  const known = wire?.known === true;
  const seen = new Set<string>();
  const rows: DomainRow[] = [];
  for (const r of known ? (wire?.results ?? []) : []) {
    const domain = text(r?.domain).toLowerCase();
    if (!domain || seen.has(domain)) continue;
    seen.add(domain);
    const available = r.available === true;
    const priceFirstYear = wireNumber(r.priceFirstYear);
    const buyUrl = checkoutUrl(r.buyUrl);
    rows.push({
      domain,
      status: domainStatus(available, r.freeWithYearlyPlan === true, priceFirstYear),
      priceFirstYear,
      priceRenewal: wireNumber(r.priceRenewal),
      buyUrl,
      selectable: available && buyUrl !== '',
    });
    if (rows.length === DOMAIN_RESULTS_MAX) break;
  }
  return {
    state: !known ? 'error' : rows.length === 0 ? 'empty' : 'ready',
    currency: known ? currencyCode(wire?.currency) : '',
    query: known ? text(wire?.query) : '',
    connectedDomain: known ? text(wire?.connectedDomain).toLowerCase() : '',
    rows,
    defaultDomain: rows.find((r) => r.selectable)?.domain ?? '',
    failure: failureOf(wire),
    metaSiteId: text(wire?.metaSiteId),
  };
}

/* -------------------------------------------------------------------------------------------- fallback to Wix */

export type SitePage = 'plans' | 'domain';

/** Percent-encode everything but the RFC 3986 unreserved characters (A-Z a-z 0-9 - . _ ~), as iOS and Android do. */
function encodeStrict(value: string): string {
  return encodeURIComponent(value).replace(/[!'()*]/g, (ch) => `%${ch.charCodeAt(0).toString(16).toUpperCase()}`);
}

/**
 * Where a page that could not load sends the reader instead of an error (Ronny, 2026-10-26): Wix's own plans / domain
 * page for the Space's business site. The site is the one the answer named, else the one the Space's website tile
 * knows; with neither, the account's subscriptions / domains list. The domain page carries the search (as
 * {@link domainQuery} reads it) when there is one.
 */
export function wixFallbackUrl(page: SitePage, metaSiteId: string, siteMetaSiteId: string, query: string): string {
  const msid = text(metaSiteId) || text(siteMetaSiteId);
  if (page === 'plans') {
    return msid
      ? `https://manage.wix.com/premium-pricing/wix/select-plan?siteGuid=${encodeStrict(msid)}&referralAdditionalInfo=symphony`
      : 'https://manage.wix.com/account/subscriptions';
  }
  if (!msid) return 'https://manage.wix.com/account/domains';
  const q = domainQuery(query);
  return `https://manage.wix.com/get-domain?msid=${encodeStrict(msid)}&flowType=purchase&referralAdditionalInfo=symphony${q ? `&domainName=${encodeStrict(q)}` : ''}`;
}

/* -------------------------------------------------------------------------------------------- routes + copy */

/** The Plans page; `workspace/<slug>/plan` deeplinks resolve to it on web. */
export function sitePlansPath(slug: string): string {
  return `/workspace/${encodeURIComponent(slug)}/plan`;
}

/** The Domain page; `workspace/<slug>/domain` deeplinks resolve to it on web. */
export function siteDomainPath(slug: string): string {
  return `/workspace/${encodeURIComponent(slug)}/domain`;
}

export const SITE_PLANS_ASK_PROMPT = 'sitePlans.askPrompt';
export const SITE_DOMAIN_ASK_PROMPT = 'siteDomain.askPrompt';
